import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

/**
 * LoadingSpinner component - Displays a centered loading spinner with optional message
 *
 * @param {boolean} fullScreen - If true, spans full viewport height
 * @param {string} message - Optional loading message to display below spinner (plain text)
 * @param {string} messageId - Optional translation key for i18n message (takes precedence over message)
 * @param {string} size - Size of the spinner ('small', 'medium', 'large')
 * @param {string} backgroundColor - Optional background color for the container
 * @param {string} textColor - Optional text color for the message
 * @param {string} logoSrc - Optional logo image source to display above spinner
 * @returns {JSX.Element} Loading spinner component
 */
function LoadingSpinner({
  fullScreen = false,
  message = "",
  messageId = null,
  size = "medium",
  backgroundColor = null,
  textColor = null,
  logoSrc = null,
}) {
  const sizeMap = {
    small: 30,
    medium: 40,
    large: 60,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: fullScreen ? "var(--app-vh)" : "100%",
        minHeight: fullScreen ? "unset" : "200px",
        gap: 2,
        backgroundColor: backgroundColor || "transparent",
      }}
    >
      {logoSrc && (
        <Box
          component="img"
          src={logoSrc}
          alt="Earth Map Logo"
          sx={{
            maxWidth: "300px",
            maxHeight: "100px",
            width: "auto",
            height: "auto",
            marginBottom: 2,
          }}
        />
      )}
      <CircularProgress
        size={sizeMap[size]}
        sx={textColor ? { color: textColor } : undefined}
      />
      {(messageId || message) && (
        <Typography variant="body2" color={textColor || "text.secondary"}>
          {messageId ? <FormattedMessage id={messageId} /> : message}
        </Typography>
      )}
    </Box>
  );
}

LoadingSpinner.propTypes = {
  fullScreen: PropTypes.bool,
  message: PropTypes.string,
  messageId: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  logoSrc: PropTypes.string,
};

export default LoadingSpinner;
